import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { ReadOnlyNotice } from '@/components/shared/read-only-notice';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import { useIsPlatformAdmin } from '@/hooks/use-is-platform-admin';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

interface PlatformConsoleSession {
  rememberMeEnabled: boolean;
  idleTimeoutMinutes: number;
}

const IDLE_MIN = 5;
const IDLE_MAX = 240;

export const ConsoleSessionPage = () => {
  const { t } = useTranslation('admin');
  const isAdmin = useIsPlatformAdmin();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [rememberMeEnabled, setRememberMeEnabled] = useState(true);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(String(15));
  const [idleError, setIdleError] = useState<string | undefined>();

  const config = useQuery({
    queryKey: ['platform-console-session'],
    queryFn: () => api.get<PlatformConsoleSession>('/platform/console-session'),
  });

  useEffect(() => {
    if (!config.data) return;
    setRememberMeEnabled(config.data.rememberMeEnabled);
    setIdleTimeoutMinutes(String(config.data.idleTimeoutMinutes));
  }, [config.data]);

  const save = useMutation({
    mutationFn: (body: PlatformConsoleSession) => api.patch('/platform/console-session', body),
    onSuccess: async () => {
      toast.success(t('consoleSession.saved'));
      await queryClient.invalidateQueries({ queryKey: ['platform-console-session'] });
      await queryClient.invalidateQueries({ queryKey: ['auth', 'console-session'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const onSave = () => {
    const parsed = Number(idleTimeoutMinutes.trim());
    const invalid = !Number.isInteger(parsed) || parsed < IDLE_MIN || parsed > IDLE_MAX;
    setIdleError(invalid ? t('consoleSession.idleInvalid') : undefined);
    if (invalid) return;
    save.mutate({ rememberMeEnabled, idleTimeoutMinutes: parsed });
  };

  return (
    <div>
      <PageHeader title={t('consoleSession.title')} description={t('consoleSession.description')} />
      <ReadOnlyNotice />

      <Card>
        <CardHeader>
          <CardTitle>{t('consoleSession.cardTitle')}</CardTitle>
          <CardDescription>{t('consoleSession.cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="flex items-start gap-3">
            <Checkbox
              id="rememberMeEnabled"
              checked={rememberMeEnabled}
              onCheckedChange={(checked) => setRememberMeEnabled(checked === true)}
            />
            <div className="min-w-0">
              <Label htmlFor="rememberMeEnabled" className="cursor-pointer">
                {t('consoleSession.rememberMeLabel')}
              </Label>
              <p className="mt-0.5 text-caption text-fg-secondary">{t('consoleSession.rememberMeDescription')}</p>
            </div>
          </div>

          <FormField
            label={t('consoleSession.idleLabel')}
            hint={t('consoleSession.idleHint')}
            error={idleError}
            required
          >
            {(field) => (
              <Input
                {...field}
                inputMode="numeric"
                autoComplete="off"
                value={idleTimeoutMinutes}
                onChange={(event) => {
                  setIdleTimeoutMinutes(event.target.value);
                  if (idleError) setIdleError(undefined);
                }}
              />
            )}
          </FormField>

          {isAdmin ? (
            <div className="flex justify-end">
              <Button loading={save.isPending} disabled={config.isLoading} onClick={onSave}>
                {t('consoleSession.save')}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
